import { formatAmount, getObjectKeys } from "./GenericUtils";

export function isValidEmail(email) {
  const re = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; //eslint-disable-line
  return re.test(email);
}

export function isEmailValid(email) {
  const regX = new RegExp(/^[a-zA-Z0-9_]+(\.[_a-zA-Z0-9]+)*@[a-z0-9-]+(\.[a-z0-9-]+)*(\.[a-z]{2,15})$/);
  return !(regX.test(email.toLocaleLowerCase()));
}

export function isNumberInString(str) {
  //checks for whether string has number or not
  return /\d/.test(str);
}

export function isValidString(name, allowSpaces = false, allowOnlySpaces = false) {
  // Allow letters and optionally spaces
  const isValidFormat = allowSpaces ? /^[a-zA-Z\s]*$/.test(name) : /^[a-zA-Z]+$/.test(name);

  // If allowOnlySpaces is true, allow strings containing only spaces
  if (allowOnlySpaces) {
    return name.trim() === "" || isValidFormat;
  }

  // Otherwise, ensure the string is not empty and matches the format
  return name.trim() !== "" && isValidFormat && !name.startsWith(" ") && !name.endsWith(" ");
}

// GST validation
export const isValidGSTNumber = (gstin) => {
  const regex = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}\d[Z]{1}[A-Z\d]{1}$/;
  return regex.test(gstin);
};

export const lastNamevalidation = (string) => {
  const lastNameRegex = /^(?:[A-Za-z]+(?: [A-Za-z]+)*)?$/;
  return lastNameRegex.test(string.trim());
};

export function isValidNumber(s) {
  return /^[0-9]+$/.test(s);
}

export const isAlphanumeric = (value) => {
  const alphanumericRegex = /^[a-zA-Z0-9]*$/;
  return alphanumericRegex.test(value);
};

export function isValidPhone(phone) {
  const re = /^[6-9]\d{9}$/;
  return re.test(phone);
}

export function isEmptyObject(ob) {
  if (ob === null || ob === undefined || Object.keys(ob).length < 1) {
    return true;
  } else if (Object.keys(ob).length) {
    return false;
  } else {
    return true;
  }
}

export function isEmptyArray(arr) {
  if (arr.length > 0) {
    return false;
  } else if (arr.length <= 0) {
    return true;
  }
}

export function isArray(data) {
  if (data instanceof Array) {
    return true;
  } else return false;
}

export function isObject(data) {
  if (data instanceof Object && !(data instanceof Array)) {
    return true;
  } else return false;
}

export function isEmptyObjectOrArray(data) {
  if (data instanceof Array) {
    return isEmptyArray(data);
  } else if (data instanceof Object) {
    return isEmptyObject(data);
  } else if (data === null || data === undefined) {
    return undefined;
  }
}

export function isKeyPresent(data, key) {
  let keys = [];
  if (isArray(data)) {
    keys = data;
  } else if (isObject(data)) {
    keys = getObjectKeys(data);
  }

  let index = keys.findIndex((x) => x === key);

  if (index >= 0) {
    return true;
  } else return false;
}

export function isValidData(val) {
  if (val !== null && val !== undefined && val !== "NA") {
    return true;
  } else {
    return false;
  }
}

export function isNullOrUndefined(value) {
  return value === null || value === undefined;
}

// Validation function for the contact us form
export function validateContactUsForm(data) {
  // Initialize an empty errors object
  const errors = {};

  // Validate name using isValidString
  if (!data.name || !isValidString(data.name, true)) {
    errors.name = "Full Name is required and must contain only letters and spaces.";
  }

  // Validate mobile number using a regular expression
  if (!data.mobile_number || !/^\d{10}$/.test(data.mobile_number)) {
    errors.mobile_number = "Mobile Number is required and must be exactly 10 digits.";
  }

  // Validate email using isValidEmail
  if (!data.email || !isValidEmail(data.email)) {
    errors.email = "Email is required and must be a valid email address.";
  }

  // Validate message
  if (!data.message) {
    errors.message = "Message is required.";
  }

  // Validate subject
  if (!data.subject) {
    errors.subject = "Subject is required.";
  }

  // Return validation result
  return {
    isValid: Object.keys(errors).length === 0, // Check if there are no errors
    errors,
  };
}

export const validateRangeAmount = (value, min_amount, max_amount) => {
  if (value === '' || isNaN(value)) {
    return "Amount is required and must be a number";
  }
  const amountNumber = Number(value);
  if (amountNumber < parseInt(min_amount)) {
    return `Amount must be at least ₹${formatAmount(parseInt(min_amount))}`;
  }
  if (amountNumber > parseInt(max_amount)) {
    return `Amount must not exceed ₹${formatAmount(parseInt(max_amount))}`;
  }
  return null;
};

export const validateSlabAmount = (value) => {
  if (value === '' || isNaN(value)) {
    return "Amount is required and must be a number";
  }
  return null;
};

// Auth form validation functions
export const validateLoginForm = (data, otpValidate = false) => {
  const errors = {};

  // Validate mobile number
  if (!data.mobile_number || data.mobile_number.trim() === '') {
    errors.mobile_number = "Mobile Number is required";
  } else if (data.mobile_number.length < 7) {
    errors.mobile_number = "Mobile Number must be at least 7 digits";
  }

  // Validate OTP if OTP validation is enabled
  if (otpValidate) {
    if (!data.otp || data.otp.trim() === '') {
      errors.otp = "OTP is required";
    } else if (!/^\d{6}$/.test(data.otp)) {
      errors.otp = "OTP must be exactly 6 digits";
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

export const validateSignUpForm = (data, otpValidate = false, options = {}) => {
  const {
    requireName = true,
    requireUid = false,
    uid = "",
  } = options;
  const errors = {};

  // Validate name
  if (requireName) {
    if (!data.name || data.name.trim() === '') {
      errors.name = "Full Name is required";
    } else if (!isValidString(data.name, true)) {
      errors.name = "Full Name must contain only letters and spaces";
    }
  }

  if (requireUid && !uid.trim()) {
    errors.userId = "UID is required";
  }

  // Validate mobile number
  if (!data.mobile_number || data.mobile_number.trim() === '') {
    errors.mobile_number = "Mobile Number is required";
  } else if (data.mobile_number.length < 7) {
    errors.mobile_number = "Mobile Number must be at least 7 digits";
  }

  // Validate email if provided
  if (data.email && data.email.trim() !== '') {
    if (!isValidEmail(data.email)) {
      errors.email = "Please enter a valid email address";
    }
  }

  // Validate OTP if OTP validation is enabled
  if (otpValidate) {
    if (!data.otp || data.otp.trim() === '') {
      errors.otp = "OTP is required";
    } else if (!/^\d{6}$/.test(data.otp)) {
      errors.otp = "OTP must be exactly 6 digits";
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};
